export const useCaseData = {
  answers: {
    icon: '💬',
    title: 'Quick Answers',
    subtitle: 'Query your data in plain English. Get grounded answers instantly.',
    cards: [
      {
        title: 'CEO Asking:',
        example: '"What\'s our MRR growth compared to last quarter, and which segment drove it?"',
        result: '✓ MRR grew 28% to $182K. Enterprise upgrades (+$24K) were the main driver.',
        vs: 'vs FastAPP: Same but no causal context why upgrades happened'
      },
      {
        title: 'Sales Lead Asking:',
        example: '"Show me all deals over $50K closing this month with incomplete next steps"',
        result: '✓ 7 deals totaling $420K. 4 need proposals, 3 awaiting signatures.',
        vs: 'vs Manual: 45 min of filtering. With Layrs: 2 seconds.'
      },
      {
        title: 'Ops Lead Asking:',
        example: '"Which projects are over budget and by how much?"',
        result: '✓ 3 projects over budget: Design Sprint (+$12K), API Migration (+$8K), Mobile App (+$5K).',
        vs: 'vs Manual: 3 hours of spreadsheet work. With Layrs: 2 seconds.'
      }
    ],
    metricsTitle: 'Time Saved',
    metrics: [
      { label: 'Time per query', value: '2 sec', description: 'vs 30-180 min manual' },
      { label: 'Queries per day', value: '50+', description: 'Previously 2-3 max' },
      { label: 'Hours saved/week', value: '15+', description: 'Per team member' }
    ]
  },
  predict: {
    icon: '🔮',
    title: 'Predict Outcomes',
    subtitle: 'Forecast revenue, churn, and opportunities with confidence intervals.',
    cards: [
      {
        title: 'Predict Churn:',
        example: '"Which customers will churn in 30 days?"',
        result: '✓ 12 accounts at risk. 70% PPV (actual churn). Save $25K ARR.',
        vs: 'vs Correlation tools: 40% false positives. Wastes time.'
      },
      {
        title: 'Forecast Cash:',
        example: '"Predict next month\'s cash collection"',
        result: '✓ $145K expected. ±$8K confidence interval. DSO drops 20%.',
        vs: 'vs Manual forecast: Inaccurate. Outdated weekly.'
      },
      {
        title: 'Growth Opportunities:',
        example: '"Which SMB accounts will upgrade to Enterprise?"',
        result: '✓ 23 accounts likely to upgrade. Expected value: $120K ARR.',
        vs: 'vs Guessing: Hit rate 15%. With Layrs: 65% hit rate.'
      }
    ],
    metricsTitle: 'Prediction Quality',
    metrics: [
      { label: 'Accuracy (Churn)', value: '85%+', description: 'vs 60% correlation tools' },
      { label: 'False Positives', value: '30%', description: 'vs 60% traditional ML' },
      { label: 'Time to Prediction', value: '30 sec', description: 'vs weeks for data science' }
    ]
  },
  explain: {
    icon: '🔍',
    title: 'Explain Changes',
    subtitle: 'When metrics move, Layrs shows why—with causal chains, not just correlations.',
    cards: [
      {
        title: 'Revenue Dropped:',
        example: '"Revenue down 15%. What happened?"',
        result: '✓ Day 4: Release bug → Day 3: Support tickets +300% → Day 1: Churn spike. Fix: Roll back release.',
        vs: 'vs Flow: Timeline only. Layrs: Causal chain + action.'
      },
      {
        title: 'Sales Team Underperforming:',
        example: '"Why is West region 20% below target?"',
        result: '✓ 2 reps on PTO + new competitor entered + sales cycle lengthened. Impact: -18%.',
        vs: 'vs Manual: Guesswork. Layrs: Quantified causes.'
      },
      {
        title: 'Support Tickets Spiked:',
        example: '"Support volume up 40%. Why?"',
        result: '✓ New feature rolled out → Integration breaking for 30% of users → Tickets +300%. Resolution: Hotfix.',
        vs: 'vs Dashboards: Alert only. Layrs: Root cause + fix.'
      }
    ],
    metricsTitle: 'Root Cause Analysis',
    metrics: [
      { label: 'Time to root cause', value: '5 min', description: 'vs 2-3 days manual' },
      { label: 'Accuracy', value: '92%', description: 'First diagnosis correct' },
      { label: 'False causes', value: '8%', description: 'Flagged for review' }
    ]
  },
  simulate: {
    icon: '⚡',
    title: 'Simulate Scenarios',
    subtitle: 'Run what-ifs instantly. Predict outcomes before you decide.',
    cards: [
      {
        title: 'Pricing Decision:',
        example: '"What if we raise Enterprise tier from $10K to $12K/year?"',
        result: '✓ Revenue +$48K/year. Churn risk: 5%. Net impact: +$45K. Confidence: 82%.',
        vs: 'vs Manual A/B test: 4 weeks, $50K cost. Layrs: 30 sec, $0 cost.'
      },
      {
        title: 'Feature Launch:',
        example: '"If we launch new feature, how many customers upgrade?"',
        result: '✓ 18 upgrades expected. ARR impact: +$72K. Churn reduction: 3%. Confidence: 78%.',
        vs: 'vs Guessing: Wrong 50% of time. Layrs: 78% confidence interval.'
      },
      {
        title: 'Marketing Budget:',
        example: '"Increase marketing spend by 50%. What\'s the revenue impact?"',
        result: '✓ Revenue +$120K/year. CAC improves to $800. Payback: 4.2 months. Confidence: 71%.',
        vs: 'vs Excel models: Outdated. Layrs: Real-time, data-driven.'
      }
    ],
    metricsTitle: 'Decision Quality',
    metrics: [
      { label: 'Time to simulation', value: '30 sec', description: 'vs weeks for analysis' },
      { label: 'Scenarios tested', value: '10+', description: 'vs 1-2 manually' },
      { label: 'Confidence intervals', value: 'Quantified', description: 'vs hunches' }
    ]
  },
  anomaly: {
    icon: '🚨',
    title: 'Detect Anomalies',
    subtitle: 'Spot unusual patterns instantly. Layrs explains what\'s wrong before you notice.',
    cards: [
      {
        title: 'Sales Anomaly:',
        example: 'System notices: "Sales down 12% vs normal Monday"',
        result: '✓ Root cause found: 3 top reps on PTO. Expected to recover +18% Tuesday.',
        vs: 'vs Dashboards: Alert only, no explanation. Layrs: Alert + diagnosis.'
      },
      {
        title: 'Churn Spike:',
        example: 'System notices: "Churn 40% higher than expected this week"',
        result: '✓ Root cause: Billing system delayed. 8 customers churned due to invoice issues.',
        vs: 'vs Manual: Discovered after 2 days. Layrs: Real-time detection.'
      },
      {
        title: 'Support Load:',
        example: 'System notices: "Support tickets 3x normal volume"',
        result: '✓ Root cause: Email integration broken. Affecting 12% of users. ETA fix: 2 hours.',
        vs: 'vs Waiting for reports: 1-2 hour delay. Layrs: Instant detection + diagnosis.'
      }
    ],
    metricsTitle: 'Anomaly Detection',
    metrics: [
      { label: 'Detection speed', value: 'Real-time', description: 'vs daily reports' },
      { label: 'Explanations', value: 'Automatic', description: 'vs none' },
      { label: 'False alarms', value: '5%', description: 'High signal quality' }
    ]
  },
  audit: {
    icon: '📋',
    title: 'Audit Decisions',
    subtitle: 'Full transparency trail. Prove why you made each decision. Regulatory compliant.',
    cards: [
      {
        title: 'Board Question:',
        example: '"Why did you raise prices 10%?"',
        result: '✓ Causal analysis: Pricing change causes 8% churn but 18% revenue lift. Net: +$120K. Confidence: 84%. Auditable.',
        vs: 'vs Gut feeling: "We thought it was right." Layrs: Data-driven proof.'
      },
      {
        title: 'Compliance Audit:',
        example: '"Prove your dunning strategy improves cash collection"',
        result: '✓ Full audit trail: Changed reminder timing → DSO dropped 18% → $120K recovered. All decisions logged with confidence.',
        vs: 'vs Manual logs: Incomplete. Layrs: Complete causal audit.'
      },
      {
        title: 'Risk Review:',
        example: '"Did our churn prediction model help or hurt?"',
        result: '✓ Accuracy: 85%. Prevented 12 churns = $48K saved. 2 false positives = $5K wasted. Net: +$43K. ROI: 8.6x.',
        vs: 'vs Assumption: "Probably helped." Layrs: Quantified impact proof.'
      }
    ],
    metricsTitle: 'Audit Quality',
    metrics: [
      { label: 'Audit trail', value: '100%', description: 'Every decision logged' },
      { label: 'Compliance ready', value: '✓', description: 'GDPR, SOC2, etc' },
      { label: 'Proof quality', value: 'Causal', description: 'Not just correlation' }
    ]
  },
  autonomous: {
    icon: '🤖',
    title: 'Autonomous Actions',
    subtitle: 'System detects problems, simulates fixes, takes action—all automatically.',
    cards: [
      {
        title: 'Auto-Dunning:',
        example: 'System detects: "Invoice overdue 30 days from high-value customer"',
        result: '✓ Simulates: Reminder → -5% likely churn. Sends reminder. Creates follow-up task. Tracks if it worked.',
        vs: 'vs Manual reminders: Forgotten. Layrs: Autonomous, with proof.'
      },
      {
        title: 'Churn Prevention:',
        example: 'System predicts: "Enterprise account at 85% churn risk"',
        result: '✓ Tests interventions: Priority support → 22% churn reduction. Creates support ticket. Logs impact.',
        vs: 'vs Waiting for alerts: Too late. Layrs: Proactive intervention.'
      },
      {
        title: 'Auto-Scaling:',
        example: 'System notices: "Lead quality declining 15% per week trend"',
        result: '✓ Simulates: Tighten filters → 20% reduction in MQLs but 30% higher conversion. Updates filter. Monitors.',
        vs: 'vs Manual tuning: Weeks of guessing. Layrs: Autonomous optimization.'
      }
    ],
    metricsTitle: 'Automation Quality',
    metrics: [
      { label: 'Actions per week', value: '100+', description: 'vs manual 2-3' },
      { label: 'Success rate', value: '78%', description: 'Causal reasoning' },
      { label: 'Human review', value: 'Optional', description: 'You stay in control' }
    ]
  }
};
